// src/services/blocking.js
//
// 🟢 BLOCKING — the phone's exact model, which is two mirrored arrays:
//
//   users/{me}.blockedUsers  — whom I blocked
//   users/{me}.blockedBy     — who blocked me (written onto MY doc by THEIR client)
//
// Both directions therefore arrive on ONE listener: my own user doc. Nobody ever reads
// the other party's doc for block state, which is also why blocking from here reaches
// the phone instantly — the app has this same listener on users/{me}
// (HomeScreen.tsx:9882) and every one of its guards reads the arrays it feeds.
//
// 🟥 ENFORCEMENT IS CLIENT-SIDE EVERYWHERE, including on the phone (the server relays
//    without checking — verified). So the web must carry ALL its own guards: composer,
//    send, presence, typing, and the forward picker. A guard skipped here isn't
//    backstopped anywhere.

import { useEffect, useState } from 'react';
import { doc, updateDoc, onSnapshot, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../config/firebase';

export const EMPTY_BLOCKING = { byMe: new Set(), me: new Set() };

/* 🚫 A MODULE-LEVEL COPY OF THE LATEST SNAPSHOT — the app's blockStore twin.
   `watchBlocking` publishes into it, so readers that are NOT descendants of
   App.jsx (the call service, the avatar, the share sheet) can ask synchronously
   without the prop being threaded to them. */
let _now = EMPTY_BLOCKING;
const _subs = new Set();
export function publishBlocking(next) {
  _now = next || EMPTY_BLOCKING;
  for (const fn of Array.from(_subs)) { try { fn(_now); } catch {} }
}
export function blockingNow() { return _now; }
/** Did I block them? */
export function iBlockedNow(uid) { return !!uid && _now.byMe.has(uid); }
/** Did they block me? */
export function blockedMeNow(uid) { return !!uid && _now.me.has(uid); }
/** Either direction — "this door is closed". */
export function blockedEitherNow(uid) { return iBlockedNow(uid) || blockedMeNow(uid); }
/** Re-render when the block set changes. */
export function useBlocking() {
  const [s, setS] = useState(_now);
  useEffect(() => { _subs.add(setS); setS(_now); return () => { _subs.delete(setS); }; }, []);
  return s;
}
/** The picture I am allowed to show for `uid`: theirs, unless one of us blocked the other. */
export function visiblePhoto(uid, photo) { return blockedEitherNow(uid) ? null : photo; }

/** cb({ byMe: Set<uid I blocked>, me: Set<uid who blocked me> }) — live. */
export function watchBlocking(uid, cb) {
  if (!uid) return () => {};
  return onSnapshot(doc(db, 'users', uid), (snap) => {
    const d = snap.data() || {};
    const next = {
      byMe: new Set(Array.isArray(d.blockedUsers) ? d.blockedUsers : []),
      me: new Set(Array.isArray(d.blockedBy) ? d.blockedBy : []),
    };
    publishBlocking(next);   // 🚫 the module copy first, so sync readers agree with the render
    cb(next);
  }, () => { publishBlocking(EMPTY_BLOCKING); cb(EMPTY_BLOCKING); });
}

/** The same two writes the app makes (ChatModal.tsx:10193): mine and the mirror. */
export async function blockUser(myUid, targetUid) {
  await updateDoc(doc(db, 'users', myUid), { blockedUsers: arrayUnion(targetUid) });
  // the mirror is what lets THEM find out, through their own doc listener
  await updateDoc(doc(db, 'users', targetUid), { blockedBy: arrayUnion(myUid) }).catch(() => {});
  publishBlocking({ byMe: new Set([...Array.from(_now.byMe), targetUid]), me: _now.me });   // 🚫 optimistic
}

export async function unblockUser(myUid, targetUid) {
  await updateDoc(doc(db, 'users', myUid), { blockedUsers: arrayRemove(targetUid) });
  await updateDoc(doc(db, 'users', targetUid), { blockedBy: arrayRemove(myUid) }).catch(() => {});
  publishBlocking({ byMe: new Set(Array.from(_now.byMe).filter((x) => x !== targetUid)), me: _now.me });   // 🚫 optimistic
}

/** Either direction — the app treats both as "this conversation is closed". */
export function isBlockedEither(blocking, uid) {
  if (!blocking || !uid) return false;
  return blocking.byMe.has(uid) || blocking.me.has(uid);
}
